import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowRightLeft, Loader2, AlertCircle } from 'lucide-react';
import { userApiClient } from '../../api/HttpClient';
import { API_GET_LONGTERM_BEDS } from '../../api/config';

interface Room {
  id: string;
  room_number: string;
}

interface Bed {
  id: string;
  bed_code: string;
  status: 'vacant' | 'occupied';
}

interface ChangeBedDialogProps {
  open: boolean;
  onClose: () => void;
  currentBedId: string;
  currentBedCode: string;
  propertyId: string;
  onBedChanged: () => void;
}

const ChangeBedDialog: React.FC<ChangeBedDialogProps> = ({
  open,
  onClose,
  currentBedId,
  currentBedCode,
  propertyId,
  onBedChanged,
}) => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [bedsByRoom, setBedsByRoom] = useState<Record<string, Bed[]>>({});
  const [selectedBedId, setSelectedBedId] = useState<string | null>(null);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedBedId(null);
    setError(null);
    fetchRoomsAndBeds();
  }, [open, propertyId]);

  const fetchRoomsAndBeds = async () => {
    setLoadingRooms(true);
    try {
      const client = await userApiClient;
      const fetchedRooms = await client.get<Room[]>(
        `/api/pg_tenant/longterm/properties/${propertyId}/rooms/`
      );
      setRooms(fetchedRooms);

      const bedsMap: Record<string, Bed[]> = {};
      await Promise.all(
        fetchedRooms.map(async (room) => {
          const url = API_GET_LONGTERM_BEDS.replace('roomID', room.id);
          const beds = await client.get<Bed[]>(url);
          bedsMap[room.id] = beds.filter((b) => b.status === 'vacant');
        })
      );
      setBedsByRoom(bedsMap);
    } catch (err: any) {
      setError('Failed to load rooms and beds.');
    } finally {
      setLoadingRooms(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedBedId) return;
    setSubmitting(true);
    setError(null);
    try {
      const client = await userApiClient;
      await client.patch(`/api/pg_tenant/longterm/beds/${currentBedId}/change-bed/`, {
        new_bed_id: selectedBedId,
      });
      onBedChanged();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to change bed.');
    } finally {
      setSubmitting(false);
    }
  };

  const vacantBedCount = Object.values(bedsByRoom).flat().length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] bg-card p-0 gap-0 overflow-hidden">
        <div className="bg-primary px-6 py-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white text-lg font-semibold">
              <ArrowRightLeft className="h-5 w-5" />
              Change Bed
            </DialogTitle>
            <DialogDescription className="text-primary-foreground/80 text-sm mt-0.5">
              Current bed: <span className="font-medium">{currentBedCode}</span> — select a vacant bed to move tenant to
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[420px] overflow-y-auto">
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          {loadingRooms ? (
            <div className="flex justify-center items-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : vacantBedCount === 0 ? (
            <div className="text-center text-muted-foreground py-10 text-sm">
              No vacant beds available in this property.
            </div>
          ) : (
            rooms.map((room) => {
              const vacantBeds = bedsByRoom[room.id] ?? [];
              if (vacantBeds.length === 0) return null;
              return (
                <div key={room.id}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Room {room.room_number}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {vacantBeds.map((bed) => (
                      <button
                        key={bed.id}
                        onClick={() => setSelectedBedId(bed.id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                          selectedBedId === bed.id
                            ? 'border-primary bg-primary/10 text-primary font-medium'
                            : 'border-border hover:border-primary/50 hover:bg-muted/50'
                        }`}
                      >
                        {bed.bed_code}
                        <Badge className="bg-green-500/10 text-green-600 text-xs px-1.5 py-0">
                          Vacant
                        </Badge>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedBedId || submitting}
            className="flex-1"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Moving…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4" />
                Confirm Change
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeBedDialog;
